/**
 * sl-m82f.4.1: Static configuration for the disjoint feature tabs.
 * sl-4wjh.3.1: Extended with contexts field for context-based filtering.
 *
 * Order defines the rendered tab sequence. Feature-gated tabs are filtered
 * out at render time based on `ServerFeature` membership.
 */
import type { TabConfig } from './types';

const I18N_PREFIX =
  'com.streamlain.settings.workspace.ai.analytics.tab.' as const;

export const TAB_CONFIGS: readonly TabConfig[] = [
  {
    key: 'ai_chat',
    labelKey: `${I18N_PREFIX}ai-chat`,
    defaultLabel: 'AI Chat',
    featureKey: 'aiChat',
    contexts: ['user', 'workflow'],
  },
  {
    key: 'intelligent-search',
    labelKey: `${I18N_PREFIX}intelligent-search`,
    defaultLabel: 'Intelligent Search',
    featureKey: 'intelligentSearch',
    requiredFeature: 'CopilotEmbedding',
    contexts: ['workspace'],
  },
  {
    key: 'ai-memory',
    labelKey: `${I18N_PREFIX}ai-memory`,
    defaultLabel: 'AI Memory',
    featureKey: 'aiMemory',
    requiredFeature: 'CopilotMemory',
    // ai-memory runs background (auto-summary bots) without a user_id —
    // belongs in the workspace "background costs" tab, not user.
    contexts: ['workspace'],
  },
  {
    key: 'business-intelligence',
    labelKey: `${I18N_PREFIX}business-intelligence`,
    defaultLabel: 'Business Intelligence',
    featureKey: 'businessIntelligence',
    requiredFeature: 'BusinessIntelligence',
    contexts: ['workspace'],
  },
  {
    key: 'image-gen',
    labelKey: `${I18N_PREFIX}image-gen`,
    defaultLabel: 'Image Generation',
    featureKey: 'imageGen',
    contexts: ['user', 'workflow'],
    hideIfZero: true,
  },
  {
    key: 'transcription',
    labelKey: `${I18N_PREFIX}transcription`,
    defaultLabel: 'Transcription',
    featureKey: 'transcription',
    contexts: ['user', 'workflow'],
    hideIfZero: true,
  },
  {
    key: 'websearch',
    labelKey: `${I18N_PREFIX}websearch`,
    defaultLabel: 'Web Search',
    featureKey: 'websearch',
    contexts: ['user'],
    hideIfZero: true,
  },
  {
    key: 'workflows',
    labelKey: `${I18N_PREFIX}workflows`,
    defaultLabel: 'Workflows',
    featureKey: 'workflows',
    // Contexts list is only consulted when the context tab-bar is visible.
    // This tab is rendered exclusively via `onlyTab="workflows"` on the
    // workspace:workflows settings page (the workspace:ai dialog
    // explicitly excludes it via excludeTabs). Any `contexts` value is
    // effectively a no-op but we keep the prior committed 'user' so the
    // field stays type-consistent with the rest of the config.
    contexts: ['user'],
  },
] as const;
